var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import VectorSource from "ol/source/Vector";
import { ColorManager } from "./color";
import { FeatureData } from "./data";
import { StyleManager } from "./styling";
import VectorLayer from "ol/layer/Vector";
import { Observable, Refresh, WaitFor } from "../../misc/decorators";
import { Switchboard } from "../../events";
import { Maybe, Some } from "../../misc/option";
import { get_sequential_diff } from "../../misc/array";
let SelectableLayer = class SelectableLayer {
    constructor(opts) {
        this.name = "selectableLayer";
        this.colorized = false;
        this.receiver = Switchboard.get_receiver("SELECTION");
        this.raw = new VectorLayer({
            source: new VectorSource({ overlaps: false })
        });
        this.source = this.raw.getSource();
        this.url = opts.url;
        this.identifier = opts.identifier;
        this.data = new FeatureData(opts, this.identifier);
        this.color_manager = opts.color_manager ? opts.color_manager : new ColorManager({ color_range: { start_color: '#FF0000', end_color: '#0000FF', num_colors: 5, opacity: 0.7 }, strategies: [] });
        this.style_manager = opts.style_manager ? opts.style_manager : new StyleManager(this.identifier, () => this.data.clicked, () => this.data.hovered, new Some(() => this.color_manager.ignored_colors));
        this.map = opts === null || opts === void 0 ? void 0 : opts.map;
        this.style_manager.style_function.map(sf => this.raw.setStyle(sf));
        this.receiver.on('change', () => this.changed());
    }
    set projection(val) {
        this.data.projection = val;
    }
    get features() {
        if (!this.source)
            return [];
        return this.source.getFeatures();
    }
    get ids() { return this.data.ids; }
    get hovered() { return this.data.hovered; }
    set hovered(val) {
        this.data.hovered = val;
    }
    get clicked() { return this.data.clicked; }
    set clicked(val) {
        this.data.clicked = val;
    }
    get style() { return this.style_manager.style; }
    update_style(style_name, mode, alt) {
        this.style_manager.update_style(style_name, mode, alt);
        this.style_manager.style_function.map(fn => this.raw.setStyle(fn));
    }
    id_of(f) {
        return this.data.id_of(f);
    }
    changed() {
        const current = this.features.map(f => this.id_of(f));
        const next = this.data.ids;
        const { adds: adds, subs: subs } = get_sequential_diff(current, next);
        for (let id of adds) {
            const feature = Maybe(this.data.features.find(f => this.id_of(f) === id));
            feature.map(f => { var _a; return (_a = this.source) === null || _a === void 0 ? void 0 : _a.addFeature(f); });
        }
        for (let id of subs) {
            const feature = Maybe(this.features.find(f => this.id_of(f) === id));
            feature.map(f => { var _a; return (_a = this.source) === null || _a === void 0 ? void 0 : _a.removeFeature(f); });
        }
        this.raw.changed();
    }
    clear() { var _a; (_a = this.source) === null || _a === void 0 ? void 0 : _a.clear(); this.data.clear(); }
    async select_feature(id) {
        if (!id)
            return;
        await this.data.select_feature_by_id(id);
    }
    async get_feature_by_id(id) {
        let feature = Maybe(this.features.find(f => this.id_of(f) === id));
        if (feature.isSome())
            return feature;
        return await this.data.get_feature_by_id(id);
    }
    async get_features_by_id(ids) {
        let features = [];
        for (let id of ids) {
            let f = await this.get_feature_by_id(id);
            if (f.isSome()) {
                features.push(f.unwrap());
            }
        }
        return features;
    }
    async get_feature_center_by_id(id) {
        return await this.data.get_feature_center_by_id(id);
    }
    async add_features_by_ids(ids, focused) {
        await this.data.add_features_by_ids(ids, focused);
    }
    remove_features_by_ids(ids) {
        this.data.remove_features_by_ids(ids);
    }
    remove_features_except(id) {
        this.data.remove_features_by_ids(this.ids.filter(i => i != id));
    }
    async add_feature_at_point(coordinate, _alt = false) {
        await this.data.add_feature_at_point(coordinate);
    }
    remove_feature_at_point(coordinate) {
        this.data.remove_feature_at_point(coordinate);
    }
    async add_features_by_geometry(geom) {
        await this.data.add_features_by_geometry(geom);
    }
    remove_features_by_geometry(geom) {
        this.data.remove_features_by_geometry(geom);
    }
    downloadKml() {
        var _a;
        (_a = this.map) === null || _a === void 0 ? void 0 : _a.download_kml_data(this.features, new Date().toISOString().replace('Z', ''));
    }
};
__decorate([
    Refresh
], SelectableLayer.prototype, "update_style", null);
__decorate([
    WaitFor()
], SelectableLayer.prototype, "add_features_by_ids", null);
__decorate([
    WaitFor()
], SelectableLayer.prototype, "add_feature_at_point", null);
__decorate([
    WaitFor()
], SelectableLayer.prototype, "add_features_by_geometry", null);
SelectableLayer = __decorate([
    Observable
], SelectableLayer);
export { SelectableLayer };
